// HRPersonDetailModal — detail osoby, osm sekcí (Profil, Upomínky, Bonusy a srážky,
// Dokumenty, Finance, Dovolené, Testování, Historie).
//
// Načítá detail z /api/hr-get-person?id=X.
// Profil tab: textová pole, edit mode, Save tlačítko (PATCH /api/hr-update-person).
// Dokumenty tab: upload + download + delete (POST/GET/DELETE /api/hr-file).
// Foto: klik na avatar v hlavičce → upload (POST /api/hr-photo).
// Delete osoby (owner only): tlačítko vpravo dole → ConfirmDialog → DELETE /api/hr-delete-person.
//
// RBAC: pokud backend vrátí redacted=true, zobrazí se jen Profil tab s read-only kontakty.

import { confirmDialog } from "./confirmHost.jsx";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { X as XIcon, User, FileText, Wallet, Calendar, History, Save, Pencil, Upload, Download, Trash2, Mail, Briefcase, Award, Building2, AlertCircle, Lock, Loader2, Plus, ExternalLink, AlertTriangle, Coins, ChevronDown, ChevronRight, TrendingUp, TrendingDown, GraduationCap, Plane, EyeOff } from "lucide-react";
import { apiFetch, useApiData } from "../lib/api.js";
import useEscClose from "../lib/useEscClose.js";
import CopyButton from "./CopyButton.jsx";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { Zalozky, Modal, HlavickaOkna, OdznakOkna, ListovaniMesice, SkeletonRows, EmptyState, ErrorNote } from "./ui.jsx";
import HRUpominkaAddModal from "./HRUpominkaAddModal.jsx";
import HRBonusSrazkaAddModal from "./HRBonusSrazkaAddModal.jsx";
import { formatDate, formatDateTime, fmt, fmtKc } from "../lib/format.js";
import { toast } from "./Toast.jsx";
import { LOKALITY } from "../lib/lokality.js";

const TABS = [
  { id: "profil", label: "Profil", icon: User },
  { id: "upominky", label: "Upomínky", icon: AlertTriangle },
  { id: "bonusy-srazky", label: "Bonusy a srážky", icon: Award },
  { id: "dokumenty", label: "Dokumenty", icon: FileText },
  { id: "finance", label: "Finance", icon: Wallet, ownerOnly: true },
  { id: "dovolene", label: "Dovolené", icon: Plane },
  { id: "testovani", label: "Testování", icon: GraduationCap },
  { id: "historie", label: "Historie", icon: History },
];

const POZICE_OPTIONS = ["CEO", "Head Coach Senior", "Head Coach Junior", "Trener", "Nutricni specialista", "Clients Manager", "Sales", "Marketing", "HR", "IT"];
const TYP_SPOLUPRACE_OPTIONS = ["HPP", "DPP", "DPC", "OSVČ"];
// 4.9.2026: jediný seznam lokalit je v src/lib/lokality.js (dřív byl tenhle
// a druhý, jinak psaný, v src/pages/Trainers.jsx).
const LOKALITA_OPTIONS = LOKALITY;

function poziceLabel(p) {
  return p === "Trener" ? "Trenér" : (p === "Nutricni specialista" ? "Nutriční specialista" : p || "—");
}

// Délka spolupráce ze zacatek_spoluprace (YYYY-MM-DD) do dneška.
// Vrátí např. "2 měsíce", "1 rok 3 měsíce", "3 roky".
function formatDelkaSpoluprace(zacatekIso) {
  if (!zacatekIso) return "—";
  const from = new Date(zacatekIso + "T00:00:00");
  if (isNaN(from.getTime())) return "—";
  const now = new Date();
  let months = (now.getFullYear() - from.getFullYear()) * 12 + (now.getMonth() - from.getMonth());
  if (now.getDate() < from.getDate()) months--;
  months = Math.max(0, months);
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const yearLabel = (n) => n === 1 ? "rok" : (n < 5 ? "roky" : "let");
  const monthLabel = (n) => n === 1 ? "měsíc" : (n < 5 ? "měsíce" : "měsíců");
  if (months === 0) return "tento měsíc";
  if (years === 0) return `${months} ${monthLabel(months)}`;
  if (remMonths === 0) return `${years} ${yearLabel(years)}`;
  return `${years} ${yearLabel(years)} ${remMonths} ${monthLabel(remMonths)}`;
}

export default function HRPersonDetailModal({ personId, initialData, onClose, onChanged }) {
  useEscClose(onClose); // Esc zavre detail (vnorene dialogy maji prednost)
  const user = getCurrentUser();
  const isOwner = user?.role === "owner";

  // 22.5.2026: instant rendering — pokud Lide.jsx propaguje initialData
  // (řádek tabulky), modal renderuje OKAMŽITĚ z těchto dat, /api/hr-get-person
  // pak doplní jen JSONB pole (smlouvy/nda/dokumenty/socialni_site).
  const [person, setPerson] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("profil");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  // 14.9.2026 (fáze 6d): v patce byla tři tlačítka, na telefonu se nevešla
  // vedle sebe a Trvale smazat, tedy nejnebezpečnější akce, bylo to nejnápadnější
  // v okně. Teď je v patce koš a ten otevře volbu, kde je konečně napsané, čím
  // se deaktivace liší od trvalého smazání.
  const [volbaMazani, setVolbaMazani] = useState(false);

  const loadPerson = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/hr-get-person?id=${encodeURIComponent(personId)}`);
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setPerson(j.person);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPerson(); /* eslint-disable-next-line */ }, [personId]);

  const handleDelete = async () => {
    try {
      const r = await apiFetch(`/api/hr-delete-person?id=${encodeURIComponent(personId)}&confirm=ANO_SMAZAT`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setConfirmDelete(false);
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  // HARD DELETE — trvalé smazání. Owner only, dvojitý confirm + warning.
  const handleHardDelete = async () => {
    try {
      const r = await apiFetch(`/api/hr-delete-person?id=${encodeURIComponent(personId)}&mode=hard&confirm=ANO_NATVRDO`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setConfirmHardDelete(false);
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  const odznakyOsoby = person ? (
    <>
      {person.pozice && <OdznakOkna>{poziceLabel(person.pozice)}</OdznakOkna>}
      {person.typ_spoluprace && <OdznakOkna>{person.typ_spoluprace}</OdznakOkna>}
      {person.zacatek_spoluprace && (
        <OdznakOkna ton="zeleny">{formatDelkaSpoluprace(person.zacatek_spoluprace)}</OdznakOkna>
      )}
      {(isOwner || (user?.role === "manager" && person.is_trener)) && person.sazba_info?.sazba > 0 && (
        <OdznakOkna ton="zluty">{person.sazba_info.sazba} Kč/h</OdznakOkna>
      )}
      {(isOwner || (user?.role === "manager" && person.is_trener)) && !person.is_trener && person.odmena > 0 && (
        <OdznakOkna ton="zluty">{person.odmena.toLocaleString("cs-CZ")} Kč/měs</OdznakOkna>
      )}
      {person.active === false ? (
        <OdznakOkna ton="sedy">Bývalý</OdznakOkna>
      ) : /ukon/i.test(String(person.status || "")) ? (
        <OdznakOkna ton="zluty">Ukončeno, jen Fakturace</OdznakOkna>
      ) : (
        <OdznakOkna ton="zeleny">Aktivní</OdznakOkna>
      )}
      {person.na_dovolene && <OdznakOkna ton="modry">Na dovolené</OdznakOkna>}
      {person.ma_narozeniny && <OdznakOkna ton="ruzovy">Narozeniny</OdznakOkna>}
      {person.redacted && <OdznakOkna ton="zluty">Detail skrytý</OdznakOkna>}
    </>
  ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      plnaHlavicka
      header={
        <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-5">
          <div className="flex items-center gap-3">
            {person && <PersonAvatar person={person} editable={isOwner || (user?.role === "manager" && person?.is_trener)} onUpdated={() => { loadPerson(); onChanged && onChanged(); }} />}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-serif text-lg font-bold leading-tight demo-sensitive">{person?.display_name || "—"}</span>
              {odznakyOsoby && <span className="mt-1 flex flex-wrap gap-1.5">{odznakyOsoby}</span>}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              title="Zavřít"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-white/20"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      }
      footer={!loading && person ? (
        <>
          {isOwner && (
            <button
              type="button"
              onClick={() => setVolbaMazani(true)}
              aria-label="Deaktivovat nebo smazat"
              title="Deaktivovat nebo smazat"
              className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-rose-200 text-rose-500 transition active:scale-95 sm:hover:bg-rose-50"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          )}
          <button onClick={onClose} className="btn btn-primary">Zavřít</button>
        </>
      ) : undefined}
    >
        {/* Sekce. Osm položek, takže Zalozky ze vzorníku samy použijí rozbalovací
            výběr. Dřív to byly podtržítkové záložky, ze kterých byly na telefonu
            vidět dvě a půl a zbytek se musel hledat tahem do strany.
            12.9.2026: Finance vidí jen owner, endpoint hr-vyplaty cizí detail
            nikomu jinému nevrací a prázdná sekce by jen mátla. */}
        {/* 12.9.2026: obal měl vlastní px-4, jenže tělo okna ho má taky, takže
            byl výběr o 16 px užší než všechno pod ním. Záporný okraj vrátí
            oddělovač přes celou šířku a výběr srovná se zbytkem obsahu. */}
        <div className="-mx-4 mb-4 border-b border-slate-200 px-4 pb-4 sm:-mx-5 sm:px-5">
          <Zalozky
            stitek="Sekce"
            aria-label="Sekce detailu osoby"
            value={activeTab}
            onChange={setActiveTab}
            items={TABS.filter((tab) => !tab.ownerOnly || isOwner).map((tab) => ({
              key: tab.id,
              label: tab.label,
              ikona: tab.icon,
            }))}
          />
        </div>

        <div>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-3 text-sm text-slate-500">Načítám detail…</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" /> {error}
            </div>
          )}
          {!loading && !error && person && (
            <>
              {activeTab === "profil" && (
                <ProfilTab person={person} isOwner={isOwner} onSaved={() => { loadPerson(); onChanged && onChanged(); }} />
              )}
              {activeTab === "upominky" && (
                <UpominkyTab person={person} isOwner={isOwner} />
              )}
              {activeTab === "bonusy-srazky" && (
                <BonusySrazkyTab person={person} isOwner={isOwner} />
              )}
              {activeTab === "dokumenty" && (
                <DokumentyTab person={person} canEdit={isOwner || (user?.role === "manager" && person.is_trener)} onChanged={() => loadPerson()} />
              )}
              {activeTab === "finance" && <FinanceTab person={person} />}
              {activeTab === "dovolene" && <DovoleneTab person={person} />}
              {activeTab === "testovani" && <TestovaniTab person={person} />}
              {activeTab === "historie" && <HistorieTab personId={person.id} />}
            </>
          )}
        </div>


      <Modal
        open={volbaMazani}
        onClose={() => setVolbaMazani(false)}
        size="sm"
        header={
          <HlavickaOkna
            ikona={Trash2}
            nazev={`Co s ${person?.display_name || "osobou"}?`}
            popis="Vyber, jestli ji jen schovat, nebo úplně vymazat."
            onClose={() => setVolbaMazani(false)}
          />
        }
        footer={<button type="button" onClick={() => setVolbaMazani(false)} className="btn btn-secondary">Zrušit</button>}
      >
        <div className="space-y-2">
          {person?.active && (
            <button
              type="button"
              onClick={() => { setVolbaMazani(false); setConfirmDelete(true); }}
              className="w-full rounded-2xl border border-slate-200 p-3 text-left transition sm:hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 shrink-0 text-slate-500" />
                <span className="text-[15px] font-bold text-slate-900">Deaktivovat</span>
                <span className="flex-1" />
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[13px] font-bold text-emerald-700">vratné</span>
              </div>
              <div className="mt-1 text-[13px] leading-relaxed text-slate-500">
                Zmizí ze seznamu aktivních, ale zůstane v systému. Historie, odpracované hodiny,
                výplaty i dokumenty zůstávají. Kdykoli ji vrátíš zpátky.
              </div>
            </button>
          )}
          <button
            type="button"
            onClick={() => { setVolbaMazani(false); setConfirmHardDelete(true); }}
            className="w-full rounded-2xl border border-rose-200 bg-rose-50/50 p-3 text-left transition sm:hover:bg-rose-50"
          >
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 shrink-0 text-rose-600" />
              <span className="text-[15px] font-bold text-rose-700">Trvale smazat</span>
              <span className="flex-1" />
              <span className="rounded-full bg-rose-200 px-2 py-0.5 text-[13px] font-bold text-rose-800">nevratné</span>
            </div>
            <div className="mt-1 text-[13px] leading-relaxed text-rose-700/80">
              Zmizí z databáze. Historie zůstane, ale přestane být napojená na člověka.
              Používej jen na chybný nebo dvakrát založený záznam.
            </div>
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Deaktivovat osobu?"
        description={`${person?.display_name || "Osoba"} se označí jako neaktivní ke dnešnímu dni. Zmizí ze seznamu aktivních, ale všechna data zůstanou a kdykoli ji vrátíš zpátky.`}
        confirmLabel="Deaktivovat"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
        variant="danger"
      />
      <ConfirmDialog
        open={confirmHardDelete}
        title="Opravdu trvale smazat?"
        description={`${person?.display_name || "Osoba"} se smaže z databáze. Odpracované hodiny, fakturace i záznamy v audit logu zůstanou, jen se přestanou hlásit k ní. Fotka a soubory zůstávají taky. Tohle už nejde vrátit, používej to jen na chybný nebo dvakrát založený záznam.`}
        confirmLabel="Smazat"
        onConfirm={handleHardDelete}
        onCancel={() => setConfirmHardDelete(false)}
        variant="danger"
      />
    </Modal>
  );
}

// ── Profil tab — SEKCE-BASED EDIT (jako ClientDetail) ─────────────────
//
// Každá karta má vlastní "Upravit" tlačítko v hlavičce. Po kliku se pole
// rozbalí jako editovatelné inputs, dole "Uložit" + "Zrušit". Save odešle
// VŠECHNA změněná pole najednou (1 PATCH request per sekce).
//
// Owner+manager (mimo redacted). Email read-only (přes admin-update-trainer-email).

function ProfilTab({ person, isOwner, onSaved }) {
  const [saveError, setSaveError] = useState(null);
  const canEdit = !person.redacted;

  // Bulk save handler — sekce mu předá objekt změn { telefon, slack_id, ... }
  // 28.5.2026: Email — pro trenéra (is_trener=true) jde extra přes admin-update-trainer-email
  // (synchronizuje uzivatele tabulku + login). Pro non-trenéra rovnou v PATCH.
  const saveSection = async (changes) => {
    setSaveError(null);
    // Select umi jen text, backend ceka boolean - prevod na jednom miste.
    if (changes.dpp_aktivni !== undefined && typeof changes.dpp_aktivni === "string") {
      changes = { ...changes, dpp_aktivni: changes.dpp_aktivni === "Ano" };
    }
    try {
      // 1) Pokud se mění email + osoba je trenér → routovat na admin endpoint nejdřív.
      //    POZOR: endpoint má dry-run by default, musí se poslat confirm:"yes" jinak nic neudělá.
      if (changes.email !== undefined && person.is_trener) {
        const newEmail = String(changes.email || "").trim().toLowerCase();
        if (newEmail) {
          const r = await apiFetch("/api/admin-update-trainer-email", {
            method: "POST",
            body: JSON.stringify({ trenerId: person.id, newEmail, confirm: "yes" }),
          });
          const j = await r.json();
          if (!r.ok) throw new Error(j.error || `Email sync HTTP ${r.status}`);
          if (j.noop) {
            // Email je stejný jako stávající — nic se nemění, OK.
          } else if (j.changes && j.changes.errors && j.changes.errors.length > 0) {
            // Některý ze sub-stepů selhal (PG / Sheet / uzivatele) — surface to user.
            const errMsg = j.changes.errors.map((e) => `${e.step}: ${e.error}`).join(", ");
            throw new Error(`Email se neuložil čistě: ${errMsg}`);
          }
        }
        // Odstranit email ze zbytku změn — admin endpoint už ho zpracoval.
        const rest = { ...changes };
        delete rest.email;
        if (Object.keys(rest).length === 0) {
          onSaved && onSaved();
          return { ok: true };
        }
        changes = rest;
      }
      // 2) Ostatní pole (i email pro non-trenéry) rovnou na hr-update-person.
      const r = await apiFetch(`/api/hr-update-person?id=${encodeURIComponent(person.id)}`, {
        method: "PATCH",
        body: JSON.stringify(changes),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved && onSaved();
      return { ok: true };
    } catch (e) {
      setSaveError(e.message);
      return { ok: false, error: e.message };
    }
  };

  return (
    <div className="space-y-5">
      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Detail této osoby je skrytý dle role. Vidíš jen základní kontakty.
        </div>
      )}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
          Chyba při ukládání: {saveError}
        </div>
      )}

      {/* Redesign 16.7.2026: JEDEN sloupec (dva sloupce s pastelovymi kartami
          byly nepřehledné), nahoře viditelný přepínač stavu spolupráce. */}
      <div className="max-w-2xl mx-auto space-y-4">

        <StatusSection person={person} editable={canEdit} onSaved={onSaved} />

        {/* Karta: Kontakty */}
        <EditableSection
          title="Kontakty" icon={Mail} accent="violet"
          editable={canEdit}
          fields={[
            { key: "display_name", label: "Jméno a příjmení", type: "text" },
            { key: "email", label: "Email", type: "text", copy: true, hint: person.is_trener ? "U trenéra se kromě HR DB synchronizuje i s tabulkou Uživatelé (login)." : null },
            { key: "telefon", label: "Telefon", type: "tel", copy: true },
            { key: "slack_id", label: "Slack ID", type: "text" },
          ]}
          person={person}
          onSave={saveSection}
        />

        {/* Karta: Spolupráce */}
        <EditableSection
          title="Spolupráce" icon={Briefcase} accent="emerald"
          editable={canEdit}
          fields={[
            { key: "pozice", label: "Pozice", type: "select", options: POZICE_OPTIONS, renderOption: poziceLabel },
            { key: "typ_spoluprace", label: "Typ spolupráce", type: "select", options: TYP_SPOLUPRACE_OPTIONS },
            { key: "lokalita", label: "Lokalita", type: "select", options: LOKALITA_OPTIONS },
            { key: "zacatek_spoluprace", label: "Začátek spolupráce", type: "date" },
            { key: "datum_konce", label: "Konec spolupráce", type: "date", hint: "Vyplň při ukončení. U trenéra se k tomuto dni zmrazí sazba za tréninkovou jednotku, dál už neroste." },
          ]}
          person={person}
          onSave={saveSection}
        />

        {/* Karta: Fakturační — jen pokud ne redacted */}
        {!person.redacted && (
          <EditableSection
            title="Fakturační údaje" icon={Building2} accent="sky"
            editable={canEdit}
            fields={[
              { key: "ico", label: "IČO", type: "text", copy: true },
              { key: "dic", label: "DIČ", type: "text", copy: true },
              { key: "bankovni_ucet", label: "Bankovní účet", type: "text", copy: true },
              { key: "adresa_fakturace", label: "Adresa pro fakturaci", type: "textarea" },
            ]}
            person={person}
            onSave={saveSection}
          />
        )}

        {/* Karta: Osobní — jen pokud ne redacted */}
        {!person.redacted && (
          <EditableSection
            title="Osobní" icon={User} accent="amber"
            editable={canEdit}
            fields={[
              { key: "narozeniny", label: "Narozeniny (den a měsíc)", type: "text", hint: "Např. 16.1. V ten den přijde přání do Slacku a člověk má v HR štítek." },
              { key: "datum_narozeni", label: "Datum narození", type: "date" },
              { key: "tisnovy_kontakt", label: "Tísňový kontakt", type: "text", hint: "Jméno + telefon blízké osoby." },
              { key: "velikost_tricka", label: "Velikost trička", type: "text", hint: "XS/S/M/L/XL/XXL pro merch." },
            ]}
            person={person}
            onSave={saveSection}
          />
        )}

        {/* Karta: Sazby (owner-only) — jen pokud ne redacted.
            Sazby se POČÍTAJÍ AUTOMATICKY ze Smlouvy A.2 (trenerSazba.cjs tabulka tierů) + hardcoded výjimky.
            Owner zde může jen přepnout režim (Smlouva/Výjimka) a manuálně nastavit částku pokud Výjimka. */}
        {!person.redacted && isOwner && (
          <EditableSection
            title="Sazby a odměna" icon={Wallet} accent="rose"
            editable={true}
            fields={[
              { key: "sazba_rezim", label: "Režim výpočtu sazby", type: "select", options: ["Smlouva", "Vyjimka"], hint: "Smlouva = automaticky podle délky spolupráce. Výjimka = ručně nastavená sazba." },
              { key: "sazba_vyjimka", label: "Sazba výjimka (Kč/h)", type: "number", hint: "Použije se POUZE pokud režim = Výjimka." },
              { key: "odmena", label: "Fixní odměna měsíčně (Kč)", type: "number", hint: "Pro HPP/DPP (non-trenéři) místo sazby za hodinu." },
              { key: "dpp_aktivni", label: "Má aktivní DPP", type: "select", options: ["Ano", "Ne"], hint: "Ukazuje se v přehledu DPP hodin. Přepnout jde i přímo tam." },
            ]}
            person={person}
            onSave={saveSection}
          />
        )}
      </div>
    </div>
  );
}

// ── StatusSection — viditelný přepínač stavu spolupráce (16.7.2026) ──
// Dřív byl status schovaný (malé "Neaktivní" v hlavičce + Deaktivovat
// v patičce). Teď je první věc v Profilu: Aktivní / Ukončeno / Bývalý.
function StatusSection({ person, editable, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Optimisticky zvoleny stav (17.7.2026): tlacitko se prepne HNED po kliknuti,
  // PATCH bezi na pozadi. Kdyz selze, vratime puvodni stav + chybova hlaska.
  const [opt, setOpt] = useState(null);
  const jeUkonceny = /ukon/i.test(String(person.status || ""));
  const ulozeny = person.active === false ? "byvaly" : (jeUkonceny ? "ukonceno" : "aktivni");
  const stav = opt || ulozeny;
  useEffect(() => { setOpt(null); }, [person.status, person.active]);
  const MOZNOSTI = [
    { key: "aktivni", label: "Aktivní",
      popis: "Běžná spolupráce, všechno dostupné.",
      on: "bg-emerald-600 border-emerald-600 text-white shadow-sm" },
    { key: "ukonceno", label: "Ukončeno",
      popis: "V odchodu: zůstává ve výplatách a vyúčtování, v aplikaci vidí už jen Fakturaci. Po 45 dnech se automaticky převede na Bývalý.",
      on: "bg-amber-500 border-amber-500 text-white shadow-sm" },
    { key: "byvaly", label: "Bývalý",
      popis: "Spolupráce skončila: mimo výplaty, kapacity i aktivní seznamy.",
      on: "bg-slate-700 border-slate-700 text-white shadow-sm" },
  ];
  const nastav = async (cil) => {
    if (cil === stav || busy || !editable) return;
    // 12.9.2026: Ukončeno se dřív přepnulo hned po kliknutí, bez potvrzení.
    // Omylem přepnutý trenér přišel o úkoly a do Slacku šla výzva k předání
    // klientů. Obě odchodové volby se proto potvrzují, Aktivní zůstává bez okna.
    if (cil === "ukonceno" && !await confirmDialog({
      title: `Ukončit spolupráci s ${person.display_name}?`,
      description: "Hned po potvrzení se jeho vlastní úkoly archivují, úkoly ke klientům čekají na nového trenéra, do Slacku #automatizace odejde výzva k předání klientů a v Operations vznikne úkol pro managera. Za 45 dní se stav sám změní na Bývalý.",
      confirmLabel: "Ano, ukončit",
      cancelLabel: "Zpět",
      variant: "warning",
    })) return;
    if (cil === "byvaly" && !await confirmDialog({
      title: `Převést ${person.display_name} na Bývalý?`,
      description: "Zmizí z výplat, kapacit a aktivních seznamů a ztratí přístup do aplikace.",
      confirmLabel: "Ano, převést",
      cancelLabel: "Zpět",
      variant: "danger",
    })) return;
    setOpt(cil); setBusy(true); setErr(null);
    const body = cil === "aktivni" ? { status: "Aktivní", active: true }
      : cil === "ukonceno" ? { status: "Ukončeno", active: true }
      : { status: "Bývalý", active: false };
    try {
      const r = await apiFetch(`/api/hr-update-person?id=${encodeURIComponent(person.id)}`, {
        method: "PATCH", body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved && onSaved();
    } catch (e) { setErr(e.message); setOpt(null); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3">
      <h4 className="text-[17px] font-bold text-slate-900">Stav spolupráce</h4>
      <div className="flex gap-2">
        {MOZNOSTI.map((m) => (
          <button key={m.key} type="button" onClick={() => nastav(m.key)} disabled={!editable}
            className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-colors disabled:opacity-60 ${
              stav === m.key ? m.on : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"
            }`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="text-sm text-slate-500 leading-relaxed">
        {MOZNOSTI.find((m) => m.key === stav)?.popis}
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">{err}</div>}
    </div>
  );
}

// ── EditableSection — sekce-based edit (jako ClientDetail) ───────────
// Read mode: zobrazí pole jako FieldGrid, v hlavičce Upravit button.
// Edit mode: pole jako inputs, dole Uložit + Zrušit + "Máš neuložené změny" hint.
// Save = 1 PATCH request se všemi změnami najednou.
function EditableSection({ title, icon, accent = "violet", editable, fields, person, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    const init = {};
    for (const f of fields) init[f.key] = person[f.key] != null ? person[f.key] : "";
    setDraft(init);
    setError(null);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft({}); setError(null); };
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const hasChanges = useMemo(() => {
    for (const f of fields) {
      if (f.readOnly) continue;
      const orig = person[f.key] != null ? String(person[f.key]) : "";
      const next = draft[f.key] != null ? String(draft[f.key]) : "";
      if (orig !== next) return true;
    }
    return false;
  }, [draft, person, fields]);

  const doSave = async () => {
    if (!hasChanges) { setEditing(false); return; }
    setSaving(true);
    const changes = {};
    for (const f of fields) {
      if (f.readOnly) continue;
      const orig = person[f.key] != null ? String(person[f.key]) : "";
      const next = draft[f.key] != null ? String(draft[f.key]) : "";
      if (orig !== next) changes[f.key] = draft[f.key];
    }
    const result = await onSave(changes);
    setSaving(false);
    if (result.ok) {
      setEditing(false);
      setDraft({});
    } else {
      setError(result.error || "Chyba při ukládání");
    }
  };

  return (
    <Card
      title={title}
      icon={icon}
      accent={accent}
      action={editable && !editing ? (
        <button
          onClick={startEdit}
          className="btn btn-secondary btn-sm"
        >
          <Pencil className="w-3 h-3 inline mr-1" /> Upravit
        </button>
      ) : null}
    >
      {editing ? (
        <div className="space-y-3">
          {fields.map((f) => (
            <EditFieldRow key={f.key} field={f} value={draft[f.key]} onChange={(v) => setField(f.key, v)} disabled={saving || f.readOnly} />
          ))}
          {error && <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-sm text-rose-700">{error}</div>}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="btn btn-secondary"
            >
              Zrušit
            </button>
            <button
              onClick={doSave}
              disabled={!hasChanges || saving}
              className={`btn text-white ${
                accent === "rose" ? "bg-rose-600 hover:bg-rose-700"
                : accent === "emerald" ? "bg-emerald-600 hover:bg-emerald-700"
                : accent === "sky" ? "bg-sky-600 hover:bg-sky-700"
                : accent === "amber" ? "bg-amber-600 hover:bg-amber-700"
                : "bg-brand-600 hover:bg-brand-700"
              }`}
            >
              {saving ? "Ukládám…" : "Uložit"}
            </button>
            {hasChanges && <span className="text-sm text-amber-600 ml-1">Máš neuložené změny</span>}
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {fields.map((f) => (
            <ReadFieldRow key={f.key} field={f} value={person[f.key]} />
          ))}
        </div>
      )}
    </Card>
  );
}

function EditFieldRow({ field, value, onChange, disabled }) {
  const f = field;
  return (
    <div>
      <label className="text-[13px] uppercase tracking-wide text-slate-600 font-bold block mb-1">{f.label}</label>
      {f.readOnly ? (
        <div className="px-2 py-1.5 text-sm text-slate-500 bg-slate-100 border border-slate-200 rounded-lg italic demo-sensitive">{value || "—"}</div>
      ) : f.type === "select" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="field w-full"
        >
          <option value="">Vyber</option>
          {f.options.map((o) => (
            <option key={o} value={o}>{f.renderOption ? f.renderOption(o) : o}</option>
          ))}
        </select>
      ) : f.type === "textarea" ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={2}
          className="field w-full resize-none"
        />
      ) : (
        <input
          type={f.type || "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="field w-full"
        />
      )}
      {f.hint && <div className="text-[13px] text-slate-500 mt-0.5">{f.hint}</div>}
    </div>
  );
}

function ReadFieldRow({ field, value }) {
  const f = field;
  const display = f.renderOption && value ? f.renderOption(value) : value;
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 text-base">
      <span className="text-[15px] font-medium text-slate-600 shrink-0">{f.label}</span>
      <span className="flex items-start justify-end gap-1.5 min-w-0">
        {f.copy && value ? <CopyButton value={value} className="mt-0.5" title={`Zkopírovat ${f.label.toLowerCase()}`} /> : null}
        <span className="text-slate-900 font-semibold demo-sensitive min-w-0 break-words text-right">
          {display || <span className="text-slate-300 font-normal">—</span>}
        </span>
      </span>
    </div>
  );
}

// ── Card komponenta pro skupinu polí ──────────────────────────────────
function Card({ title, icon: Icon, accent = "violet", action, children }) {
  // Redesign 16.7.2026: jednotné bílé karty — pět pastelových gradientů
  // vedle sebe byl vizuální šum. Barvu nese jen malá ikonka.
  const iconColors = {
    violet: "text-brand-600 bg-brand-100",
    emerald: "text-emerald-600 bg-emerald-100",
    sky: "text-sky-600 bg-sky-100",
    amber: "text-amber-600 bg-amber-100",
    rose: "text-rose-600 bg-rose-100",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2 shadow-sm">
      <div className="flex items-center gap-2.5 pb-2.5 border-b border-slate-100">
        <div className={`w-9 h-9 rounded-xl ${iconColors[accent]} flex items-center justify-center`}>
          {Icon && <Icon className="w-5 h-5" />}
        </div>
        <h4 className="text-[17px] font-bold text-slate-900 flex-1">{title}</h4>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

// InlineField — read mode: text s hover edit ikonou. Click → input.
// Blur/Enter NEUKLÁDÁ rovnou — místo toho zobrazí potvrzovací lištu (Uložit / Zrušit)
// aby user mohl review změnu. Prevence překlepů. 22.5.2026.
function InlineField({ label, value, onSave, editable = true, saving = false, type = "text", textarea, hint }) {
  const [active, setActive] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [pendingConfirm, setPendingConfirm] = useState(false);
  useEffect(() => { if (!pendingConfirm) setDraft(value || ""); }, [value, pendingConfirm]);

  const askConfirm = () => {
    setActive(false);
    if (String(draft || "") !== String(value || "")) {
      setPendingConfirm(true);
    }
  };
  const cancel = () => { setDraft(value || ""); setActive(false); setPendingConfirm(false); };
  const confirmSave = () => {
    setPendingConfirm(false);
    onSave && onSave(draft);
  };

  return (
    <div>
      <label className="text-[13px] uppercase tracking-wide text-slate-600 font-bold block mb-1">{label}</label>
      {active && editable ? (
        textarea ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={askConfirm}
            onKeyDown={(e) => { if (e.key === "Escape") cancel(); }}
            autoFocus
            rows={2}
            className="field w-full resize-none"
          />
        ) : (
          <input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={askConfirm}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); askConfirm(); } if (e.key === "Escape") cancel(); }}
            autoFocus
            className="field w-full"
          />
        )
      ) : (
        <button
          type="button"
          onClick={() => editable && !pendingConfirm && setActive(true)}
          disabled={!editable}
          className={`w-full text-left px-2 py-1 text-sm rounded-lg border min-h-[1.75rem] transition-colors demo-sensitive ${
            editable
              ? "border-transparent hover:border-brand-200 hover:bg-white/60 text-slate-800 cursor-text"
              : "border-transparent text-slate-700 cursor-default"
          }`}
        >
          {pendingConfirm ? (
            <span className="text-brand-700 font-medium">{draft || <span className="text-slate-400">—</span>}</span>
          ) : (
            value ? value : <span className="text-slate-400 italic">{editable ? "klikni pro vyplnění" : "—"}</span>
          )}
          {saving && <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-brand-500" />}
        </button>
      )}
      {pendingConfirm && (
        <ConfirmBar
          oldValue={value}
          newValue={draft}
          onConfirm={confirmSave}
          onCancel={cancel}
        />
      )}
      {hint && !pendingConfirm && <div className="text-[13px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

// InlineSelect — read mode: text. Click → select dropdown. Change zobrazí confirm bar.
function InlineSelect({ label, value, onSave, editable = true, saving = false, options, renderOption }) {
  const [active, setActive] = useState(false);
  const [pendingValue, setPendingValue] = useState(null); // null = no pending
  const display = value ? (renderOption ? renderOption(value) : value) : "";

  const onChange = (newVal) => {
    setActive(false);
    if (String(newVal || "") !== String(value || "")) {
      setPendingValue(newVal);
    }
  };
  const cancel = () => { setPendingValue(null); setActive(false); };
  const confirmSave = () => {
    const v = pendingValue;
    setPendingValue(null);
    onSave && onSave(v);
  };

  return (
    <div>
      <label className="text-[13px] uppercase tracking-wide text-slate-600 font-bold block mb-1">{label}</label>
      {active && editable ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setActive(false)}
          autoFocus
          className="field w-full"
        >
          <option value="">Vyber</option>
          {options.map((o) => (
            <option key={o} value={o}>{renderOption ? renderOption(o) : o}</option>
          ))}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => editable && pendingValue == null && setActive(true)}
          disabled={!editable}
          className={`w-full text-left px-2 py-1 text-sm rounded-lg border min-h-[1.75rem] transition-colors ${
            editable
              ? "border-transparent hover:border-brand-200 hover:bg-white/60 text-slate-800 cursor-pointer"
              : "border-transparent text-slate-700 cursor-default"
          }`}
        >
          {pendingValue != null ? (
            <span className="text-brand-700 font-medium">{renderOption ? renderOption(pendingValue) : pendingValue}</span>
          ) : (
            display || <span className="text-slate-400 italic">{editable ? "klikni pro výběr" : "—"}</span>
          )}
          {saving && <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-brand-500" />}
        </button>
      )}
      {pendingValue != null && (
        <ConfirmBar
          oldValue={display || "—"}
          newValue={renderOption ? renderOption(pendingValue) : pendingValue}
          onConfirm={confirmSave}
          onCancel={cancel}
        />
      )}
    </div>
  );
}

// Confirm bar — zobrazí old → new + dvě tlačítka. Zabrání překlepu (žádný autosave).
function ConfirmBar({ oldValue, newValue, onConfirm, onCancel }) {
  return (
    <div className="mt-1 bg-amber-50 border border-amber-300 rounded-lg p-1.5 flex items-center gap-2 text-sm">
      <span className="text-amber-800 truncate flex-1">
        <span className="text-slate-500 line-through demo-sensitive">{oldValue || "—"}</span>
        <span className="mx-1 text-slate-400">→</span>
        <span className="font-semibold demo-sensitive">{newValue || "—"}</span>
      </span>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onConfirm}
        className="btn btn-primary"
      >
        Uložit
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel}
        className="btn btn-secondary"
      >
        Zrušit
      </button>
    </div>
  );
}





// ── Dokumenty tab ─────────────────────────────────────────────────────

const CATEGORIES = [
  { id: "smlouva", label: "Smlouvy", color: "violet" },
  { id: "nda", label: "NDA", color: "emerald" },
  { id: "dokument", label: "Ostatní dokumenty", color: "sky" },
  { id: "vypoved", label: "Výpověď", color: "rose" },
];

function DokumentyTab({ person, canEdit, onChanged }) {
  if (person.redacted) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <Lock className="w-4 h-4 inline mr-2" />
        Dokumenty této osoby vidí jen owner.
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {CATEGORIES.map((cat) => (
        <DocumentCategory key={cat.id} category={cat} person={person} canEdit={canEdit} onChanged={onChanged} />
      ))}
    </div>
  );
}

function DocumentCategory({ category, person, canEdit, onChanged }) {
  const key = `${category.id === "smlouva" ? "smlouvy" : category.id === "dokument" ? "dokumenty" : category.id}_keys`;
  const files = Array.isArray(person[key]) ? person[key] : [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [confirmFileKey, setConfirmFileKey] = useState(null); // soubor ke smazání (designový confirm)

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Soubor je větší než 5 MB.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      const r = await apiFetch("/api/hr-file", {
        method: "POST",
        body: JSON.stringify({ personId: person.id, category: category.id, filename: file.name, dataUrl }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onChanged && onChanged();
    } catch (e) {
      setUploadError(e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = (fileKey) => setConfirmFileKey(fileKey);
  const handleDeleteConfirmed = async (fileKey) => {
    try {
      const r = await apiFetch(`/api/hr-file?id=${encodeURIComponent(person.id)}&category=${encodeURIComponent(category.id)}&key=${encodeURIComponent(fileKey)}`, {
        method: "DELETE",
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onChanged && onChanged();
    } catch (e) {
      setUploadError(e.message);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" /> {category.label}
          <span className="text-sm text-slate-500 font-normal">({files.length})</span>
        </div>
        {canEdit && (
          <>
            <input ref={inputRef} type="file" hidden onChange={(e) => handleUpload(e.target.files?.[0])} />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-200 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Nahrát
            </button>
          </>
        )}
      </div>
      {uploadError && <div className="px-4 py-2 bg-red-50 text-sm text-red-700 border-b border-red-200">{uploadError}</div>}
      {files.length === 0 && (
        <div className="px-4 py-4 text-sm text-slate-400 text-center">Žádný soubor.</div>
      )}
      {files.map((f) => (
        <div key={f.key} className="px-4 py-2 flex items-center justify-between hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-800 truncate">{f.filename}</div>
            <div className="text-[13px] text-slate-500 mt-0.5">
              {f.size ? `${Math.round(f.size / 1024)} KB` : ""} {f.uploaded_at ? `, ${formatDate(f.uploaded_at)}` : ""}
              {f.uploaded_by ? `, ${f.uploaded_by}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <a
              href={`/api/hr-file?id=${encodeURIComponent(person.id)}&key=${encodeURIComponent(f.key)}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200"
              target="_blank" rel="noopener noreferrer"
            >
              <Download className="w-3 h-3" /> Stáhnout
            </a>
            {canEdit && (
              <button
                type="button"
                onClick={() => handleDelete(f.key)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      ))}
      <ConfirmDialog
        open={!!confirmFileKey}
        title="Smazat soubor?"
        description={<span className="break-all">{confirmFileKey}</span>}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => { const k = confirmFileKey; setConfirmFileKey(null); handleDeleteConfirmed(k); }}
        onCancel={() => setConfirmFileKey(null)}
      />
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Historie tab ──────────────────────────────────────────────────────

function HistorieTab({ personId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Filter audit log podle entity_id / details obsahujícího personId
        const r = await apiFetch(`/api/audit-log?source=hr-update-person,hr-create-person,hr-delete-person,hr-photo,hr-file&limit=50`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        const all = j.entries || j.logs || [];
        const filtered = all.filter((e) => {
          const blob = JSON.stringify(e.details || e.payload || e || {});
          return blob.includes(personId);
        });
        setLogs(filtered);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [personId]);

  if (loading) return <div className="text-sm text-slate-500 py-4 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Načítám historii…</div>;
  if (error) return <div className="text-sm text-red-700 py-4">{error}</div>;
  if (logs.length === 0) return <div className="text-sm text-slate-500 py-4 text-center">Žádné záznamy v auditu pro tuto osobu.</div>;

  return (
    <div className="space-y-2">
      {logs.map((e, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-3 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <History className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-medium text-slate-700">{e.action || e.source || "—"}</span>
            <span className="text-sm text-slate-500 ml-auto">{e.timestamp || e.created_at ? formatDateTime(e.timestamp || e.created_at) : ""}</span>
          </div>
          <div className="text-sm text-slate-500">
            {e.user || e.user_email || "—"}
            {e.result && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100">{e.result}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Upomínky tab ──────────────────────────────────────────────────────

function UpominkyTab({ person, isOwner }) {
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { data, loading, error } = useApiData(`/api/hr-upominky-list?personId=${encodeURIComponent(person.id)}#${reloadKey}`);
  // Note: reloadKey trik nefunguje s URL hash, useApiData ho zahrnuje do endpoint string a refetch.

  const entries = data?.entries || [];
  // Seskupit po měsících
  const byMonth = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.mesic)) map.set(e.mesic, []);
      map.get(e.mesic).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const handleDelete = async (id) => {
    try {
      const r = await apiFetch(`/api/hr-upominky-delete?id=${id}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setConfirmDeleteId(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Upomínky této osoby
        </h3>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="btn btn-primary"
        >
          <Plus className="w-3.5 h-3.5" /> Přidat upomínku
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500 py-4 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Načítám…</div>}
      {error && <div className="text-sm text-red-700 py-2">{error}</div>}
      {!loading && entries.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
          Žádné upomínky. Pravidlo: max 3 za měsíc, 4. a další = pokuta.
        </div>
      )}

      {byMonth.map(([mesic, items]) => {
        const pokuty = items.filter((i) => i.je_pokuta);
        const castka = pokuty.reduce((s, i) => s + (i.castka_pokuty || 0), 0);
        return (
          <div key={mesic} className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">
                {mesic} <span className="text-sm text-slate-500 font-normal">({items.length} upomínek)</span>
              </div>
              {pokuty.length > 0 && (
                <div className="text-sm font-semibold text-rose-700 flex items-center gap-1">
                  <Coins className="w-3 h-3" /> {pokuty.length} pokut, {castka.toLocaleString("cs-CZ")} Kč
                </div>
              )}
            </div>
            {items.map((e) => (
              <div key={e.id} className="px-4 py-2 flex items-start gap-3 border-b border-slate-100 last:border-b-0">
                <span className={`text-sm px-1.5 py-0.5 rounded font-bold shrink-0 ${
                  e.poradi_v_mesici >= 4 ? "bg-rose-100 text-rose-700"
                  : e.poradi_v_mesici >= 3 ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-700"
                }`}>{e.poradi_v_mesici}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">{e.duvod}</div>
                  <div className="text-[13px] text-slate-500 mt-0.5">
                    {e.datum_upominky ? formatDate(e.datum_upominky) : "—"}, {e.zadal || "—"}
                  </div>
                </div>
                {e.je_pokuta && (
                  <div className="text-sm font-semibold text-rose-700 shrink-0">
                    {e.castka_pokuty != null ? `${e.castka_pokuty.toLocaleString("cs-CZ")} Kč` : "—"}
                  </div>
                )}
                {isOwner && (
                  <button
                    onClick={() => setConfirmDeleteId(e.id)}
                    className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 shrink-0"
                    title="Smazat"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {addOpen && (
        <HRUpominkaAddModal
          defaultPersonId={person.id}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); setReloadKey((k) => k + 1); }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteId != null}
        title="Smazat upomínku?"
        description="Smazání je nevratné. Pořadí ostatních upomínek v měsíci se přepočítá."
        confirmLabel="Smazat"
        onConfirm={() => handleDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
        variant="danger"
      />
    </div>
  );
}

// ── Finance ───────────────────────────────────────────────────────────
// 12.9.2026: dřív tu byl jen text „Etapa 4". Data přitom existují, endpoint
// hr-vyplaty?mesic=&personId= vrací totéž, co okno na stránce Výplaty.
// Rozhodnutí F3: ukazuje se všechno, ale upravovat se dá dál jen ve Výplatách,
// ať je na zásahy jediné místo.

function mesicNazev(m) {
  if (!/^\d{4}-\d{2}$/.test(String(m || ""))) return m || "—";
  const [r, mm] = String(m).split("-");
  return `${MESICE_CS[parseInt(mm, 10) - 1]} ${r}`;
}
const MESICE_CS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

function posunMesic(m, o) {
  const [r, mm] = String(m).split("-").map(Number);
  const d = new Date(Date.UTC(r, mm - 1 + o, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function tentoMesic() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
// Od tohoto měsíce appka výplaty počítá, dřív se dopočítat nedá (stejná hranice
// jako v Ročním přehledu).
const PRVNI_MESIC = "2026-05";

function Udaj({ popisek, hodnota, ton = "neutralni" }) {
  const t = ton === "zelena"
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : ton === "cervena"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-white text-slate-900";
  const tp = ton === "zelena" ? "text-emerald-700" : ton === "cervena" ? "text-rose-700" : "text-slate-500";
  return (
    <div className={`rounded-xl border p-2.5 ${t}`}>
      <div className={`text-[13px] font-bold uppercase tracking-wider ${tp}`}>{popisek}</div>
      <div className="mt-0.5 text-base font-bold tabular-nums">{hodnota}</div>
    </div>
  );
}

function FinanceTab({ person }) {
  const [mesic, setMesic] = useState(tentoMesic());
  const { data, loading, error } = useApiData(
    person?.id ? `/api/hr-vyplaty?mesic=${mesic}&personId=${encodeURIComponent(person.id)}` : null
  );
  const [rozpadOtevren, setRozpadOtevren] = useState(false);

  const row = data?.row || null;
  const treninky = data?.detail?.treninky || [];

  return (
    <div className="space-y-4">
      <ListovaniMesice
        popis={mesicNazev(mesic)}
        onZpet={() => setMesic(posunMesic(mesic, -1))}
        onVpred={() => setMesic(posunMesic(mesic, 1))}
        zpetVypnuto={mesic <= PRVNI_MESIC}
        vpredVypnuto={mesic >= tentoMesic()}
      />

      {loading && <SkeletonRows rows={4} />}
      {!loading && error && <ErrorNote>{error}</ErrorNote>}
      {!loading && !error && !row && (
        <EmptyState
          icon={Wallet}
          title="Za tento měsíc nic není"
          hint="Buď osoba v tom měsíci ještě nespolupracovala, nebo se výplata nepočítá."
        />
      )}

      {!loading && row && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {row.rezim === "treninky" && (
              <>
                <Udaj popisek="Sazba / trénink" hodnota={fmtKc(Number(row.sazba) || 0)} />
                <Udaj popisek="Tréninky" hodnota={fmt(row.jednotky)} />
              </>
            )}
            {row.rezim === "fix" && <Udaj popisek="Měsíční odměna" hodnota={fmtKc(Number(row.sazba) || 0)} />}
            {row.rezim === "hodiny" && <Udaj popisek="Odměna za hodiny" hodnota={fmtKc(Number(row.zakladni_castka) || 0)} />}
            {row.rezim === "sales" && <Udaj popisek="Základ" hodnota={fmtKc(Number(row.zakladni_castka) || 0)} />}
            {/* Nula se píše bez znaménka, „−0 Kč" vypadá jako chyba. */}
            <Udaj popisek="Bonusy" hodnota={Number(row.bonusy) ? `+${fmt(row.bonusy)} Kč` : "0 Kč"} ton={Number(row.bonusy) ? "zelena" : "neutralni"} />
            <Udaj popisek="Srážky" hodnota={Number(row.srazky) ? `−${fmt(row.srazky)} Kč` : "0 Kč"} ton={Number(row.srazky) ? "cervena" : "neutralni"} />
          </div>

          <div className="rounded-xl border border-brand-200 bg-brand-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-brand-900">Celkem</span>
              <span className="shrink-0 whitespace-nowrap text-xl font-bold tabular-nums text-brand-950">{fmtKc(Number(row.celkem) || 0)}</span>
            </div>
            {row.schvaleno && <div className="mt-1 text-sm text-brand-800">Měsíc je schválený.</div>}
          </div>

          {treninky.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <button
                type="button"
                onClick={() => setRozpadOtevren((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-2.5 text-left"
              >
                <span className="text-sm font-semibold text-slate-700">Tréninky, rozpad</span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  {treninky.length}
                  {rozpadOtevren ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
              </button>
              {rozpadOtevren && (
                <div className="divide-y divide-slate-100 border-t border-slate-200">
                  {treninky.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="w-20 shrink-0 tabular-nums text-slate-500">{formatDate(t.datum)}</span>
                      <span className="min-w-0 flex-1 truncate text-slate-800">{t.klient || "—"}</span>
                      {t.propadle && (
                        <span className="shrink-0 rounded-lg bg-amber-100 px-2 py-0.5 text-[13px] font-bold text-amber-800">Propadlý</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Úpravy (motivační složka, ruční položky, ruční základ) zůstávají
              jen ve Výplatách, ať se stejná věc nedá měnit ze dvou míst. */}
          <a href={`/hr/vyplaty?mesic=${mesic}`} className="btn btn-secondary w-full">
            <ExternalLink className="h-4 w-4" /> Upravit ve Výplatách
          </a>
        </>
      )}
    </div>
  );
}

// ── Dovolené ──────────────────────────────────────────────────────────
// 12.9.2026: dřív jen text „Etapa 5". Absence v databázi jsou i s vazbou na
// osobu, endpoint dostal filtr podle person_id. Rozhodnutí D1: jen ke čtení,
// zadávat a schvalovat se dál bude na stránce Absence.

const DUVOD_POPIS = { dovolena: "Dovolená", nemoc: "Nemoc", skoleni: "Školení", jine: "Jiné" };
const DUVOD_EMOJI = { dovolena: "🏖️", nemoc: "🤒", skoleni: "📚", jine: "📌" };
const STAV_ABSENCE = {
  approved: { text: "Schváleno", cls: "bg-emerald-100 text-emerald-800" },
  pending: { text: "Čeká", cls: "bg-amber-100 text-amber-800" },
  rejected: { text: "Zamítnuto", cls: "bg-rose-100 text-rose-700" },
};

// Počet dnů včetně obou krajních dat.
function pocetDnu(od, do_) {
  const a = new Date(`${od}T00:00:00Z`);
  const b = new Date(`${do_ || od}T00:00:00Z`);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 1;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}
function denSlovem(n) {
  if (n === 1) return "1 den";
  if (n >= 2 && n <= 4) return `${n} dny`;
  return `${n} dnů`;
}

function DovoleneTab({ person }) {
  const { data, loading, error } = useApiData(
    person?.id ? `/api/hr-absence?personId=${encodeURIComponent(person.id)}` : null
  );
  const vse = useMemo(() => data?.absence || [], [data]);

  // Roky, ve kterých osoba něco má. Když je jen jeden, přepínač se nekreslí.
  const roky = useMemo(() => {
    const set = new Set(vse.map((a) => String(a.date_from || "").slice(0, 4)).filter(Boolean));
    return Array.from(set).sort();
  }, [vse]);

  const [rok, setRok] = useState(null);
  useEffect(() => {
    if (roky.length && (rok == null || !roky.includes(String(rok)))) setRok(Number(roky[roky.length - 1]));
  }, [roky.join(",")]);

  const vRoce = useMemo(
    () => vse.filter((a) => String(a.date_from || "").slice(0, 4) === String(rok)),
    [vse, rok]
  );

  // Počty se sčítají jen ze schválených, čekající a zamítnuté nic nečerpají.
  const soucty = useMemo(() => {
    const out = { dovolena: 0, nemoc: 0, skoleni: 0, jine: 0 };
    for (const a of vRoce) {
      if (a.status !== "approved") continue;
      const k = out[a.duvod] != null ? a.duvod : "jine";
      out[k] += pocetDnu(a.date_from, a.date_to);
    }
    return out;
  }, [vRoce]);

  if (loading) return <SkeletonRows rows={4} />;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!vse.length) {
    return <EmptyState icon={Plane} title="Žádné absence" hint="Zadat se dají v HR, sekce Absence." />;
  }

  return (
    <div className="space-y-4">
      {roky.length > 1 && (
        <ListovaniMesice
          popis={String(rok)}
          onZpet={() => setRok(rok - 1)}
          onVpred={() => setRok(rok + 1)}
          zpetVypnuto={String(rok) <= roky[0]}
          vpredVypnuto={String(rok) >= roky[roky.length - 1]}
        />
      )}

      <div className="grid grid-cols-2 gap-2">
        <Udaj popisek="Dovolená" hodnota={denSlovem(soucty.dovolena)} />
        <Udaj popisek="Nemoc" hodnota={denSlovem(soucty.nemoc)} />
        <Udaj popisek="Školení" hodnota={denSlovem(soucty.skoleni)} />
        <Udaj popisek="Jiné" hodnota={denSlovem(soucty.jine)} />
      </div>

      {vRoce.length === 0 ? (
        <EmptyState icon={Plane} title={`V roce ${rok} nic není`} hint="Přepni rok nad seznamem." />
      ) : (
        <div className="divide-y divide-slate-200">
          {vRoce.map((a) => {
            const st = STAV_ABSENCE[a.status] || STAV_ABSENCE.pending;
            const dnu = pocetDnu(a.date_from, a.date_to);
            return (
              <div key={a.id} className="flex items-start gap-2 py-2.5">
                <span className="text-base leading-none">{DUVOD_EMOJI[a.duvod] || "📌"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">
                    {a.date_from === a.date_to ? formatDate(a.date_from) : `${formatDate(a.date_from)} až ${formatDate(a.date_to)}`}
                  </div>
                  <div className="text-sm text-slate-500">
                    {DUVOD_POPIS[a.duvod] || a.duvod}, {denSlovem(dnu)}
                    {a.poznamka ? `, ${a.poznamka}` : ""}
                  </div>
                </div>
                <span className={`shrink-0 whitespace-nowrap rounded-lg px-2 py-0.5 text-[13px] font-bold ${st.cls}`}>
                  {st.text}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Testování ─────────────────────────────────────────────────────────
// 12.9.2026 nová sekce. Pokusy o testy v databázi byly, jen se daly vytáhnout
// pouze po jednotlivých školeních. Endpoint dostal režim podle osoby.
// Rozhodnutí T1: plochý seznam, nejnovější nahoře, každý pokus vlastní řádek.

const STAV_POKUSU = {
  prirazeno: { text: "Čeká", cls: "border-slate-200 bg-slate-100 text-slate-600" },
  probiha: { text: "Probíhá", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  hotovo: { text: "Dokončeno", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  vyprselo: { text: "Vypršelo", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  vyprselo_bez_odeslani: { text: "Vypršelo", cls: "border-rose-200 bg-rose-50 text-rose-700" },
};

function tonSkore(p) {
  if (p >= 90) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (p >= 70) return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-rose-200 bg-rose-50 text-rose-700";
}

function TestovaniTab({ person }) {
  const { data, loading, error } = useApiData(
    person?.id ? `/api/hr-skoleni?personId=${encodeURIComponent(person.id)}` : null
  );
  const pokusy = data?.pokusy || [];

  const souhrn = useMemo(() => {
    const hotove = pokusy.filter((p) => p.stav === "hotovo" && p.procent != null);
    const prumer = hotove.length
      ? Math.round(hotove.reduce((a, p) => a + Number(p.procent || 0), 0) / hotove.length)
      : null;
    return { hotovo: hotove.length, celkem: pokusy.length, prumer };
  }, [pokusy]);

  if (loading) return <SkeletonRows rows={4} />;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!pokusy.length) {
    return <EmptyState icon={GraduationCap} title="Žádné testy" hint="Přiřadit se dají v HR, sekce Školení." />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <Udaj popisek="Dokončeno" hodnota={`${souhrn.hotovo} z ${souhrn.celkem}`} />
        <Udaj popisek="Průměr" hodnota={souhrn.prumer == null ? "—" : `${souhrn.prumer} %`} />
      </div>

      <div className="divide-y divide-slate-200">
        {pokusy.map((p) => {
          const st = STAV_POKUSU[p.stav] || STAV_POKUSU.prirazeno;
          const maSkore = p.stav === "hotovo" && p.procent != null;
          const kdy = p.finished_at || p.started_at || p.prirazeno_at;
          const minut = p.trvani_sec != null ? Math.max(1, Math.round(p.trvani_sec / 60)) : null;
          return (
            <div key={p.id} className="flex items-start gap-2 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">{p.skoleni_nazev || "—"}</div>
                <div className="text-sm text-slate-500">
                  {p.stav === "prirazeno"
                    ? `Přiřazeno ${formatDate(p.prirazeno_at)}`
                    : [
                        formatDate(kdy),
                        maSkore ? `${p.spravne} z ${p.celkem}` : null,
                        minut ? `za ${minut} min` : null,
                      ].filter(Boolean).join(", ")}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {maSkore ? (
                  <span className={`whitespace-nowrap rounded-lg border px-2 py-0.5 text-[13px] font-bold tabular-nums ${tonSkore(Number(p.procent))}`}>
                    {p.procent} %
                  </span>
                ) : (
                  <span className={`whitespace-nowrap rounded-lg border px-2 py-0.5 text-[13px] font-bold ${st.cls}`}>{st.text}</span>
                )}
                {/* Nevyplněný test: odkaz, který se posílá dotyčnému. */}
                {p.stav === "prirazeno" && p.token && (
                  <CopyButton value={`${window.location.origin}/skoleni/${p.token}`} title="Zkopírovat odkaz na test" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Foto avatar v hlavičce + upload ──────────────────────────────────

function PersonAvatar({ person, editable, onUpdated }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast("Vyber obrázek.", "error"); return; }
    if (file.size > 5 * 1024 * 1024) { toast("Max 5 MB.", "error"); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const r = await apiFetch("/api/hr-photo", {
        method: "POST",
        body: JSON.stringify({ personId: person.id, dataUrl }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onUpdated && onUpdated();
    } catch (e) {
      toast("Chyba: " + e.message, "error");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const initials = String(person.display_name || "?")
    .split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative shrink-0">
      {person.foto ? (
        <img src={person.foto} alt={person.display_name} className="w-16 h-16 rounded-full object-cover border-2 border-white/30" style={{ objectPosition: "50% 25%" }} />
      ) : (
        <div className="w-16 h-16 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center font-bold text-lg">
          {initials || "?"}
        </div>
      )}
      {editable && (
        <>
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleUpload(e.target.files?.[0])} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            title="Nahrát fotku"
            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white text-slate-700 hover:bg-brand-50 flex items-center justify-center shadow-md border border-slate-200 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          </button>
        </>
      )}
    </div>
  );
}

// ── Bonusy a srážky tab ─────────────────────────────────────────────
// Accordion seřazený po měsících sestupně. Klik na hlavičku rozbalí.
// Klíč měsíce: pro stav 'vyplacen' bere mesic_vyplaceni, jinak mesic_vzniku.

const MONTHS_CS_BST = ["Leden","Únor","Březen","Duben","Květen","Červen","Červenec","Srpen","Září","Říjen","Listopad","Prosinec"];
function mesicLabelBst(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS_BST[parseInt(mm, 10) - 1]} ${y}`;
}
function fmtBst(n) { return new Intl.NumberFormat("cs-CZ").format(Math.round(n || 0)); }

const STAV_LABEL_BST = {
  caka_splneni: "Čeká splnění",
  splneno_caka_schvaleni: "Čeká schválení",
  caka_vyplaceni: "Čeká vyplacení",
  vyplacen: "Vyplaceno",
  stornovan: "Stornováno",
  neschvaleno: "Neschváleno",
};
const STAV_BADGE_BST = {
  caka_splneni: "bg-sky-100 text-sky-800 border-sky-200",
  splneno_caka_schvaleni: "bg-amber-100 text-amber-800 border-amber-200",
  caka_vyplaceni: "bg-brand-100 text-brand-800 border-brand-200",
  vyplacen: "bg-emerald-100 text-emerald-800 border-emerald-200",
  stornovan: "bg-slate-200 text-slate-600 border-slate-300",
  neschvaleno: "bg-rose-100 text-rose-800 border-rose-200",
};

function BonusySrazkyTab({ person, isOwner }) {
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("bonus");
  const { data, loading, error, refetch } = useApiData(
    person?.id ? `/api/hr-bs-list?personId=${person.id}&typ=vse&stav=vse` : null
  );

  const entries = data?.entries || [];

  // Seskupeni po mesicich (sestupne)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const key = (e.stav === "vyplacen" && e.mesic_vyplaceni) ? e.mesic_vyplaceni : (e.mesic_vzniku || "neznamy");
      if (!map.has(key)) {
        map.set(key, { mesic: key, items: [], bonusy: 0, srazky: 0, vyplaceno_bonusy: 0, vyplaceno_srazky: 0 });
      }
      const g = map.get(key);
      g.items.push(e);
      if (e.stav !== "stornovan") {
        if (e.typ === "bonus") g.bonusy += Number(e.castka);
        else g.srazky += Number(e.castka);
        if (e.stav === "vyplacen") {
          if (e.typ === "bonus") g.vyplaceno_bonusy += Number(e.castka);
          else g.vyplaceno_srazky += Number(e.castka);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.mesic < b.mesic ? 1 : -1));
  }, [entries]);

  // First month open, ostatni zavrene
  const [openMonths, setOpenMonths] = useState(new Set());
  useEffect(() => {
    if (grouped.length > 0 && openMonths.size === 0) {
      setOpenMonths(new Set([grouped[0].mesic]));
    }
  }, [grouped.length]);

  const toggleMonth = (m) => {
    const s = new Set(openMonths);
    if (s.has(m)) s.delete(m); else s.add(m);
    setOpenMonths(s);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-600">
          Celkem <b className="tabular-nums">{entries.length}</b> položek napříč {grouped.length} měsíci.
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setAddType("bonus"); setAddOpen(true); }}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" /> Bonus
          </button>
          <button
            onClick={() => { setAddType("srazka"); setAddOpen(true); }}
            className="btn btn-secondary"
          >
            <Plus className="w-4 h-4" /> Srážka
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Chyba: {error}</div>}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          <span className="ml-2 text-sm text-slate-500">Načítám…</span>
        </div>
      )}

      {!loading && grouped.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <Award className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">Tato osoba zatím nemá žádné bonusy ani srážky.</div>
        </div>
      )}

      {!loading && grouped.length > 0 && (
        <div className="space-y-2">
          {grouped.map((g) => {
            const netto = g.bonusy - g.srazky;
            const isOpen = openMonths.has(g.mesic);
            return (
              <div key={g.mesic} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleMonth(g.mesic)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-800">{mesicLabelBst(g.mesic)}</span>
                    <span className="text-sm text-slate-500">({g.items.length} položek)</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    {g.bonusy > 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <TrendingUp className="w-3.5 h-3.5" /> +{fmtBst(g.bonusy)} Kč
                      </span>
                    )}
                    {g.srazky > 0 && (
                      <span className="inline-flex items-center gap-1 text-rose-700 font-semibold">
                        <TrendingDown className="w-3.5 h-3.5" /> −{fmtBst(g.srazky)} Kč
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded font-bold tabular-nums ${
                      netto > 0 ? "bg-emerald-100 text-emerald-800" :
                      netto < 0 ? "bg-rose-100 text-rose-800" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      Netto {netto > 0 ? "+" : ""}{fmtBst(netto)} Kč
                    </span>
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-slate-100">
                    {g.items.map((e) => (
                      <div key={e.id} className="flex flex-col gap-1.5 px-4 py-2.5 sm:grid sm:grid-cols-12 sm:items-start sm:gap-2">
                        <div className="flex items-center gap-2 sm:col-span-1 sm:block">
                          <span className={`inline-flex items-center gap-0.5 text-[13px] px-1.5 py-0.5 rounded font-bold border ${
                            e.typ === "bonus" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-rose-100 text-rose-800 border-rose-200"
                          }`}>
                            {e.typ === "bonus" ? "+" : "−"}
                          </span>
                          <span className="text-[13px] tabular-nums text-slate-500 sm:hidden">
                            {e.datum_vzniku ? formatDate(e.datum_vzniku) : "—"}
                          </span>
                        </div>
                        <div className="hidden text-[13px] tabular-nums text-slate-500 sm:col-span-1 sm:block">
                          {e.datum_vzniku ? formatDate(e.datum_vzniku) : "—"}
                        </div>
                        <div className="text-sm text-slate-700 sm:col-span-6">
                          {e.popis}
                          {e.klient_name && (
                            <div className="text-[13px] text-brand-700 font-medium mt-0.5">
                              Klient: {e.klient_name}{!e.klient_id && <span className="text-amber-600 ml-1">(nový)</span>}
                            </div>
                          )}
                          {e.poznamka && <div className="text-[13px] text-slate-500 italic mt-0.5">{e.poznamka}</div>}
                          {e.duvod_storna && <div className="text-[13px] text-rose-600 mt-0.5">Důvod storna: {e.duvod_storna}</div>}
                        </div>
                        <div className="flex items-center gap-2 sm:col-span-2 sm:block sm:text-right">
                          <span className={`text-sm font-bold tabular-nums ${e.typ === "bonus" ? "text-emerald-700" : "text-rose-700"}`}>
                            {e.typ === "bonus" ? "+" : "−"}{fmtBst(e.castka)} Kč
                          </span>
                          <span className={`ml-auto inline-block rounded border px-1.5 py-0.5 text-[13px] font-medium sm:hidden ${STAV_BADGE_BST[e.stav]}`}>
                            {STAV_LABEL_BST[e.stav]}
                          </span>
                        </div>
                        <div className="hidden text-right sm:col-span-2 sm:block">
                          <span className={`inline-block text-[13px] px-1.5 py-0.5 rounded font-medium border ${STAV_BADGE_BST[e.stav]}`}>
                            {STAV_LABEL_BST[e.stav]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {addOpen && (
        <HRBonusSrazkaAddModal
          defaultTyp={addType}
          defaultPersonId={person?.id}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); refetch(); }}
        />
      )}
    </div>
  );
}
